import * as React from 'react'

import { action, makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { SitePermitStatus } from '~/client/graph'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import SitePermitAssignmentsStore from '~/client/src/shared/stores/domain/SitePermitAssignments.store'
import SitePermitFollowingsStore from '~/client/src/shared/stores/domain/SitePermitFollowings.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import ActionBar from '../../../ActionBar/ActionBar'
import PermitLogActionBarStore from '../../../PermitLogActionBar.store'
import WorkflowStatusSelector from '../../../WorkflowStatusSelector/WorkflowStatusSelector'
import PermitApprovalStore from '../../PermitApproval.store'
import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import CommonPermitButtons from './components/CommonPermitButtons'
import FormAutoActivationLabel from './components/FormAutoActivationLabel'
import InspectionPermitButtons from './components/InspectionPermitButtons'

// localization: translated

interface IProps {
  isStatusSelectorOpened: boolean
  isActionBarInReadOnlyMode: boolean
  canChangeStatus: boolean

  toggleStatusSelectMenu: () => void
  applyStatusAndStep: (status: SitePermitStatus, stepId: string) => void

  actionBarStore: PermitLogActionBarStore
  store: SitePermitCreationFormStore
  permitApprovalStore: PermitApprovalStore
  projectDateStore: ProjectDateStore
  state: InitialState

  isMobileMode?: boolean
  workflowAssignmentsStore?: SitePermitAssignmentsStore
  workflowFollowingsStore?: SitePermitFollowingsStore
}

@inject('workflowFollowingsStore', 'workflowAssignmentsStore')
@observer
export default class PermitActionBar extends React.Component<IProps> {
  @observable private localProps: IProps = null
  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(): void {
    runInAction(() => {
      if (
        this.localProps.isStatusSelectorOpened !==
        this.props.isStatusSelectorOpened
      ) {
        this.localProps.isStatusSelectorOpened =
          this.props.isStatusSelectorOpened
      }
      if (
        this.localProps.isActionBarInReadOnlyMode !==
        this.props.isActionBarInReadOnlyMode
      ) {
        this.localProps.isActionBarInReadOnlyMode =
          this.props.isActionBarInReadOnlyMode
      }
      if (this.localProps.canChangeStatus !== this.props.canChangeStatus) {
        this.localProps.canChangeStatus = this.props.canChangeStatus
      }
      if (this.localProps.isMobileMode !== this.props.isMobileMode) {
        this.localProps.isMobileMode = this.props.isMobileMode
      }
    })
  }

  public render() {
    const {
      toggleStatusSelectMenu,
      isActionBarInReadOnlyMode,
      store,
      actionBarStore,
      workflowAssignmentsStore,
      workflowFollowingsStore,
      isMobileMode,
    } = this.props
    const { editablePermit } = store

    return (
      <ActionBar
        renderStatusSelectMenu={this.renderStatusSelectMenu}
        applyStatusAndStep={this.props.applyStatusAndStep}
        store={actionBarStore}
        entity={editablePermit}
        toggleStatusSelectMenu={toggleStatusSelectMenu}
        renderButtons={this.renderButtons}
        customRenderer={this.permitStatusRenderer}
        isReadOnly={isActionBarInReadOnlyMode}
        informationalTextElement={
          store.existingPermit.isAutoActivationEnabled ? (
            <FormAutoActivationLabel store={store} />
          ) : null
        }
        isMobileMode={isMobileMode}
        assignmentsStore={workflowAssignmentsStore}
        followingsStore={workflowFollowingsStore}
        shouldShowStatusSelector={store.canChangeWorkflowStatus}
      />
    )
  }

  private renderStatusSelectMenu = (
    onClick: (status: SitePermitStatus, stepId: string) => void,
    anchor: HTMLElement,
  ) => {
    const {
      store,
      isStatusSelectorOpened,
      toggleStatusSelectMenu,
      isMobileMode,
    } = this.localProps
    const { existingPermit, template } = store

    if (!store.canChangeWorkflowStatus) {
      return null
    }

    return (
      <WorkflowStatusSelector
        anchor={anchor}
        isMobileMode={isMobileMode}
        isShown={!!isStatusSelectorOpened}
        onHide={toggleStatusSelectMenu}
        setStatusAndStep={onClick}
        currentStatus={existingPermit.status}
        currentWorkflowStepLevel={existingPermit.workflowStepLevel}
        workflowSteps={template?.stepsWithoutRecurring}
      />
    )
  }

  private renderButtons = (
    render: (
      btnText: string,
      isPermitValid: boolean,
      status: SitePermitStatus,
      isDeny?: boolean,
      customBtnHandler?: () => void,
    ) => JSX.Element,
  ) => {
    const {
      state,
      store,
      permitApprovalStore,
      projectDateStore,
      canChangeStatus,
    } = this.localProps
    const {
      template,
      workflowFieldsStore: {
        areRequiredFieldsChanged,
        areOptionalFieldsChanged,
      },
      isChangedPermitValid,
      isRecurringSettingsChanged,
    } = store

    const changeBtnText =
      areRequiredFieldsChanged || isRecurringSettingsChanged
        ? Localization.translator.change_verb
        : Localization.translator.save

    if (
      areRequiredFieldsChanged ||
      areOptionalFieldsChanged ||
      isRecurringSettingsChanged
    ) {
      return render(
        changeBtnText,
        isChangedPermitValid,
        null,
        false,
        this.updateChangedFieldsForPermit,
      )
    }

    if (!template?.type || !canChangeStatus) {
      return null
    }

    if (template?.isInspectionType) {
      return (
        <InspectionPermitButtons
          actionBarButtonsRenderer={render}
          state={state}
          store={store}
          permitApprovalStore={permitApprovalStore}
          projectDateStore={projectDateStore}
        />
      )
    }

    return (
      <CommonPermitButtons
        actionBarButtonsRenderer={render}
        store={store}
        permitApprovalStore={permitApprovalStore}
      />
    )
  }

  @action.bound
  private async updateChangedFieldsForPermit() {
    const { store } = this.props
    const {
      isChangedPermitValid,
      editablePermit,
      updatePermit,
      workflowFieldsStore: { areRequiredFieldsChanged },
    } = store

    if (!isChangedPermitValid) return

    if (areRequiredFieldsChanged) {
      // Changing what is required sends the workflow back for approval.
      if (!(await store.askToConfirmHoursWarnings('submit'))) return
      editablePermit.setStatus(SitePermitStatus.Changed)
    }
    // Always pass message for this method since it specifically handles 'Changed' status
    updatePermit(this.props.actionBarStore.draftMessage)
  }

  private permitStatusRenderer = (
    status: SitePermitStatus,
    stepId: string,
  ): JSX.Element => {
    const { template, existingPermit } = this.props.store
    const workflowStepLevel = getWorkflowStepLevel(
      stepId || existingPermit.currentWorkflowStepId,
      template.stepsWithoutRecurring,
    )
    return (
      <StatusBadge
        className="tw:mr-1"
        status={status}
        workflowStepLevel={workflowStepLevel}
        isLate={false}
      />
    )
  }
}
